<ng-template
  let-extraClass="extraClass"
  #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="taxName"
      [canGoBack]="true"
      [noBow]="true" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper [class]="isRightSideContent ? 'right-side-scroll-enabled' : ''">
    @if (loaded && loadFailed) {
      <tidy-row class="vertical-align-center">
        <ion-icon name="home" [style.color]="propertyColor" style="font-size: 16px; margin-right: 4px;" />
        <tidy-text
          [body]="propertyName"
          [translate]="false" />
      </tidy-row>
      <tidy-alert>
        <tidy-row [class]="canRetryLoad ? 'extra-bottom-padding' : ''">
          <tidy-text
            [body]="loadFailedText" />
        </tidy-row>
        @if (canRetryLoad) {
          <tidy-row>
            <tidy-button
              [text]="'Try Again'"
              [action]="boundRetry"
              class="primary" />
          </tidy-row>
        }
      </tidy-alert>
    }
    @if (loaded && !loadFailed) {
      <tidy-row class="vertical-align-center">
        <ion-icon name="home" [style.color]="propertyColor" style="font-size: 16px; margin-right: 4px;" />
        <tidy-text
          [body]="propertyName"
          [translate]="false" />
      </tidy-row>

      <tidy-card
        class="tax-accent-card"
        [ngStyle]="{ '--tax-accent-color': toneColor[statusTone] }">
        <tidy-row class="vertical-align-center">
          <span
            class="tax-chip"
            [ngStyle]="{ '--chip-fg': toneColor[statusTone], '--chip-bg': toneBg[statusTone] }">
            <span class="tax-chip__dot"></span>
            {{ statusLabel | translate }}
          </span>
        </tidy-row>
        @if (statusDetail) {
          <tidy-row class="extra-top-padding">
            <tidy-text
              [helper]="statusDetail"
              [translate]="false" />
          </tidy-row>
        }
        @if (canRegister) {
          <tidy-row class="extra-top-padding">
            <tidy-small-button
              [text]="'Mark Registered'"
              (buttonClick)="registerTax()" />
          </tidy-row>
        }
      </tidy-card>

      <tidy-row class="extra-top-padding">
        <tidy-text
          [header]="'Filings'" />
      </tidy-row>
      @if (filings.length === 0) {
        <tidy-alert>
          <tidy-row>
            <tidy-text
              [body]="'No filings yet. The first filing is scheduled after registration.'" />
          </tidy-row>
        </tidy-alert>
      }
      @if (filings.length > 0) {
        <tidy-card>
          @for (filing of filings; track $index; let last = $last) {
            <tidy-row class="vertical-align-center" [alignLastItemRight]="true">
              <tidy-text
                [body]="filing.period"
                [translate]="false" />
              <span
                class="tax-chip"
                [ngStyle]="{ '--chip-fg': toneColor[filing.tone], '--chip-bg': toneBg[filing.tone] }">
                <span class="tax-chip__dot"></span>
                {{ filing.statusLabel | translate }}
              </span>
            </tidy-row>
            <tidy-row class="vertical-align-center">
              <tidy-image
                [src]="'assets/svg/calendar-outline.svg'"
                class="body-size" />
              <tidy-text
                [helper]="filing.dateLine"
                [class]="filing.tone === 'bad' ? 'red' : ''"
                [translate]="false" />
            </tidy-row>
            @if (filing.amountLine) {
              <tidy-row class="vertical-align-center">
                <tidy-image
                  [src]="'assets/svg/cash-outline.svg'"
                  class="body-size" />
                <tidy-text
                  [helper]="filing.amountLine"
                  [translate]="false" />
              </tidy-row>
            }
            @if (filing.canMarkFiled) {
              <tidy-row>
                <tidy-text
                  [actionBody]="'Mark Filed'"
                  (action)="markFiled(filing)"
                  class="link" />
              </tidy-row>
            }
            @if (!last) {
              <tidy-row class="extra-top-padding extra-bottom-padding">
                <tidy-divider />
              </tidy-row>
            }
          }
        </tidy-card>
      }

      <tidy-row class="extra-top-padding">
        <tidy-text
          [header]="'About this Tax'" />
      </tidy-row>
      <tidy-card>
        <tidy-row class="vertical-align-center">
          <tidy-image
            [src]="'assets/svg/business-outline.svg'"
            class="body-size" />
          <tidy-text
            [helper]="jurisdictionLine"
            [translate]="false" />
        </tidy-row>
        <tidy-row class="vertical-align-center">
          <tidy-image
            [src]="'assets/svg/cash-outline.svg'"
            class="body-size" />
          <tidy-text
            [helper]="rateLine"
            [translate]="false" />
        </tidy-row>
        <tidy-row class="vertical-align-center">
          <tidy-image
            [src]="'assets/svg/repeat-outline.svg'"
            class="body-size" />
          <tidy-text
            [helper]="frequencyLine"
            [translate]="false" />
        </tidy-row>
        <tidy-row class="vertical-align-center">
          <tidy-image
            [src]="'assets/svg/document-text-outline.svg'"
            class="body-size" />
          <tidy-text
            [helper]="howToFileLine"
            [translate]="false" />
        </tidy-row>
        <tidy-row class="vertical-align-center">
          <tidy-image
            [src]="'assets/svg/receipt-outline.svg'"
            class="body-size" />
          <tidy-text
            [helper]="licenseLine"
            [translate]="false" />
        </tidy-row>
        @if (exemptionNote) {
          <tidy-row class="vertical-align-center">
            <tidy-image
              [src]="'assets/svg/information-circle-outline.svg'"
              class="body-size" />
            <tidy-text
              [helper]="'Exemptions: ' + exemptionNote"
              [translate]="false" />
          </tidy-row>
        }
      </tidy-card>

      @if (showCollection) {
        <tidy-row class="extra-top-padding">
          <tidy-text
            [header]="'Who Collects It'" />
        </tidy-row>
        <tidy-card>
          @for (platform of platformRows; track platform.key) {
            <tidy-row class="vertical-align-center" [alignLastItemRight]="true">
              <span style="display: inline-flex; align-items: center;">
                <tidy-image
                  [src]="platform.icon"
                  class="body-size" />
                <tidy-text
                  [body]="platform.label" />
              </span>
              <tidy-text
                [helper]="platform.line" />
            </tidy-row>
          }
          <tidy-row class="vertical-align-center" [alignLastItemRight]="true">
            <span style="display: inline-flex; align-items: center;">
              <tidy-image
                [src]="'assets/svg/home-outline.svg'"
                class="body-size" />
              <tidy-text
                [body]="'Direct bookings'" />
            </span>
            <tidy-text
              [helper]="'You collect'" />
          </tidy-row>
          @if (marketplaceNote) {
            <tidy-row class="vertical-align-center">
              <tidy-image
                [src]="'assets/svg/information-circle-outline.svg'"
                class="body-size" />
              <tidy-text
                [helper]="marketplaceNote"
                [translate]="false" />
            </tidy-row>
          }
        </tidy-card>
      }

      <tidy-row class="extra-top-padding vertical-align-center">
        <tidy-text
          [actionBody]="'Edit Tax'"
          (action)="editTax()"
          class="link" />
        <span>&nbsp;&nbsp;</span>
        <tidy-text
          [actionBody]="'Delete Tax'"
          (action)="confirmDelete()"
          class="red link" />
      </tidy-row>
    }
  </tidy-wrapper>
</ng-template>

@if (!isRightSideContent) {
  <app-scroll-wrapper [templateContent]="content" />
}

@if (isRightSideContent) {
  <ion-content class="ion-content-right-column">
    <ng-container *ngTemplateOutlet="content" />
  </ion-content>
}
